<script setup lang="ts">
import { inject, toRefs, watchEffect, onMounted, onUnmounted, watch } from 'vue';
import ViewOverlayGrid from '@/src/components/ViewOverlayGrid.vue';
import { useSliceConfig } from '@/src/composables/useSliceConfig';
import { Maybe } from '@/src/types';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useWindowingConfig } from '@/src/composables/useWindowingConfig';
import { useOrientationLabels } from '@/src/composables/useOrientationLabels';
import DicomQuickInfoButton from '@/src/components/DicomQuickInfoButton.vue';
import ViewTypeSwitcher from '@/src/components/ViewTypeSwitcher.vue';
import { useImage } from '@/src/composables/useCurrentImage';

interface Props {
  viewId: string;
  imageId: Maybe<string>;
}

const props = defineProps<Props>();
const { viewId, imageId } = toRefs(props);

const view = inject(VtkViewContext);
if (!view) throw new Error('No VtkView');

const { top: topLabel, left: leftLabel } = useOrientationLabels(view);

const {
  config: sliceConfig,
  slice,
  range: sliceRange,
} = useSliceConfig(viewId, imageId);

const {
  config: wlConfig,
  width: windowWidth,
  level: windowLevel,
} = useWindowingConfig(viewId, imageId);

const { metadata } = useImage(imageId);

const sendToFlutter = (type: string, data: any) => {
  const payload = { type, ...data };
  if (window.SliceSliderChannel) {
    window.SliceSliderChannel.postMessage(JSON.stringify(payload));
    console.log('📤 Sent to Flutter:', payload);
  }
};

watchEffect(() => {
  if (sliceConfig && sliceRange) {
    sendToFlutter('sliceInfo', {
      currentSlice: slice.value,
      totalSlices: sliceRange.value[1] + 1,
    });
  }
});

watchEffect(() => {
  if (wlConfig) {
    sendToFlutter('windowLevel', {
      width: windowWidth.value,
      level: windowLevel.value,
    });
  }
});

watch(slice, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    sendToFlutter('sliceChanged', {
      slice: newValue,
    });
  }
});

watch([windowWidth, windowLevel], ([newWidth, newLevel], [oldWidth, oldLevel]) => {
  if (newWidth !== oldWidth || newLevel !== oldLevel) {
    sendToFlutter('windowLevelChanged', {
      width: newWidth,
      level: newLevel,
    });
  }
});

const handleFlutterMessage = (event: MessageEvent) => {
  if (!event.data) return;

  switch (event.data.type) {
    case 'setSlice':
      if (event.data.value !== undefined) {
        slice.value = event.data.value;
        console.log('🎚️ Slice updated from Flutter:', event.data.value);
      }
      break;

    case 'setWindow':
      if (event.data.width !== undefined && event.data.level !== undefined) {
        windowWidth.value = event.data.width;
        windowLevel.value = event.data.level;
        console.log('🎚️ W/L updated from Flutter:', event.data);
      }
      break;
  }
};

onMounted(() => {
  window.addEventListener('message', handleFlutterMessage);
  console.log('✅ Flutter message listener registered');
});

onUnmounted(() => {
  window.removeEventListener('message', handleFlutterMessage);
});
</script>

<template>
  <view-overlay-grid :class="{ 'hidden-overlay': !visible }" class="overlay-no-events view-annotations">
    <template v-slot:bottom-left>
      <div class="annotation-cell">
        <div v-if="sliceConfig">
          Slice: {{ slice + 1 }}/{{ sliceRange[1] + 1 }}
        </div>
        <div v-if="wlConfig">
          W/L: {{ windowWidth.toFixed(2) }} / {{ windowLevel.toFixed(2) }}
        </div>
      </div>
    </template>
  </view-overlay-grid>
</template>

<style>
.hidden-overlay {
  display: none !important;
}
</style>

<style scoped src="@/src/components/styles/vtk-view.css"></style>
